import React, { useState, useEffect, useCallback, useRef } from 'react';
import supportInnovationApi from '../services/support-innovation';
import supportInnovationDocsApi from '../services/support-innovation-documents';
import { PAGES } from './Permissions';
import { DocumentSection } from './shared/DocumentViewer';
import { normalizeDoc, compressFile } from '../utils/documents';
import {
  STATUTS, TYPES, typeInfo, statutInfo, pageLabel, pageName, ticketNum, formatDate, relativeDate,
  copyToClipboard, Icon, JourneyTrack,
} from './SupportInnovationShared';

const fmtDateTime = (d) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// ─── Détail d'une demande (vue pleine page) ──────────────────────────────────
export default function SupportInnovationDetail({ id, user, isSuperAdmin, editMode, backLabel, onBack, onChanged, onOpenTicket, onCreateSubticket, onDeleted }) {
  const [demande, setDemande] = useState(null);
  const [commentaires, setCommentaires] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [commitRefs, setCommitRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [pasteNote, setPasteNote] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  const [editingContent, setEditingContent] = useState(false);
  const [editForm, setEditForm] = useState({ titre: '', description: '' });
  const [linkCopied, setLinkCopied] = useState(false);

  const threadEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [d, c, docs, crefs] = await Promise.all([
        supportInnovationApi.getById(id),
        supportInnovationApi.getCommentaires(id),
        supportInnovationDocsApi.getAll(id).catch(() => ({ data: { documents: [] } })),
        supportInnovationApi.getCommitRefs(id).catch(() => ({ data: { refs: [] } })),
      ]);
      setDemande(d.data.demande);
      setCommentaires(c.data.commentaires || []);
      // Demandeur : l'ouverture éteint la pastille du menu (sans effet pour le super-admin)
      supportInnovationApi.marquerVu(id).then(() => window.dispatchEvent(new Event('support:vu'))).catch(() => {});
      setDocuments((docs.data.documents || []).map(x => normalizeDoc({ ...x, isLocal: false })));
      setCommitRefs(crefs.data.refs || []);
    } catch (err) {
      setLoadError(err.response?.status === 403 || err.response?.status === 404
        ? 'Cette demande est introuvable ou ne vous appartient pas.'
        : 'Impossible de charger la demande. Réessayez dans un instant.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Mise à jour d'un ou plusieurs champs (super-admin)
  const patch = async (fields) => {
    try {
      const r = await supportInnovationApi.update(id, fields);
      setDemande(prev => ({ ...prev, ...r.data.demande }));
      onChanged && onChanged();
      return true;
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
      return false;
    }
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await supportInnovationApi.addCommentaire(id, newComment.trim());
      setNewComment('');
      const c = await supportInnovationApi.getCommentaires(id);
      setCommentaires(c.data.commentaires || []);
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setPosting(false);
    }
  };

  const saveComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    try {
      await supportInnovationApi.updateCommentaire(id, commentId, editCommentText.trim());
      setCommentaires(prev => prev.map(c => c.id === commentId ? { ...c, commentaire: editCommentText.trim() } : c));
      setEditingCommentId(null);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  // Ctrl+V d'une capture dans le message → envoyée directement en pièce jointe
  const onCommentPaste = async (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(i => i.kind === 'file' && i.type.startsWith('image/'))
      .map(i => i.getAsFile()).filter(Boolean);
    if (!files.length) return;
    e.preventDefault();
    setPasteNote('Envoi de la capture…');
    let ok = 0;
    for (const f of files) {
      try {
        const named = new File([f], `capture-${Date.now()}.${f.type.split('/')[1] || 'png'}`, { type: f.type });
        const file = await compressFile(named);
        const presign = await supportInnovationDocsApi.presign(id, { filename: file.name, contentType: file.type });
        await fetch(presign.data.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        const conf = await supportInnovationDocsApi.confirm(id, { key: presign.data.key, filename: file.name, contentType: file.type, size: file.size });
        setDocuments(prev => [...prev, { ...normalizeDoc(conf.data.document), isLocal: false }]);
        ok++;
      } catch (err) {
        console.error('[Support] capture:', err);
      }
    }
    setPasteNote(ok ? 'Capture ajoutée aux pièces jointes ✓' : 'La capture n’a pas pu être envoyée.');
    setTimeout(() => setPasteNote(''), 3500);
  };

  const copyLink = async () => {
    await copyToClipboard(`${window.location.origin}${window.location.pathname}?page=support-innovation&ticket=${id}`);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1600);
  };

  const remove = async () => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try {
      await supportInnovationApi.remove(id);
      onDeleted && onDeleted();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading && !demande) {
    return (
      <div className="sinv-detail">
        <div className="sinv-skeleton sinv-skeleton--title" />
        <div className="sinv-skeleton sinv-skeleton--track" />
        <div className="sinv-skeleton sinv-skeleton--block" />
      </div>
    );
  }
  if (loadError || !demande) {
    return (
      <div className="sinv-detail">
        <button className="sinv-link" onClick={onBack}>{Icon.arrowLeft(16)} {backLabel}</button>
        <div className="sinv-empty">
          <p>{loadError || 'Demande introuvable.'}</p>
          <button className="sinv-btn sinv-btn--soft" onClick={load}>Réessayer</button>
        </div>
      </div>
    );
  }

  const t = typeInfo(demande.type);
  const isMine = demande.user_id === user?.id;

  return (
    <article className={`sinv-detail sinv-tone-${t.tone}${isSuperAdmin ? ' sinv-detail--admin' : ''}`}>
      <div className="sinv-detail-top">
        <button className="sinv-link" onClick={onBack}>{Icon.arrowLeft(16)} {backLabel}</button>
        <button className="sinv-link" onClick={copyLink} title="Copier le lien vers cette demande">
          {Icon.link(15)} {linkCopied ? 'Lien copié ✓' : 'Copier le lien'}
        </button>
      </div>

      <header className="sinv-detail-head">
        {demande.parent_info && (
          <button className="sinv-parent-crumb" onClick={() => onOpenTicket(demande.parent_info.id)}>
            ↳ Sous-ticket de {ticketNum(demande.parent_info.ticket_number)} {demande.parent_info.titre}
          </button>
        )}
        <div className="sinv-detail-kicker">
          <span className="sinv-type-pill">{t.key === 'support' ? Icon.bug(15) : Icon.idea(15)} {t.label}</span>
          <span className="sinv-num">{ticketNum(demande.ticket_number)}</span>
        </div>
        {editingContent ? (
          <input
            className="sinv-title-input sinv-title-input--edit"
            value={editForm.titre}
            onChange={e => setEditForm(f => ({ ...f, titre: e.target.value }))}
            maxLength={255}
            aria-label="Titre"
          />
        ) : (
          <h2 className="sinv-detail-title">{demande.titre}</h2>
        )}
        <div className="sinv-detail-meta">
          <span>{isMine ? 'Envoyée' : `Par ${demande.auteur_prenom || ''} ${demande.auteur_nom || ''}${demande.concession_nom ? ` · ${demande.concession_nom}` : ''} ·`} {relativeDate(demande.created_at)}</span>
          {demande.page && <span>{pageName(demande.page)}</span>}
          {demande.date_livraison_prevue && (
            <span className="sinv-meta-strong">{Icon.calendar(14)} Prévu pour le {formatDate(demande.date_livraison_prevue)}</span>
          )}
        </div>
        <JourneyTrack statut={demande.statut} />
      </header>

      <div className="sinv-detail-grid">
        <div className="sinv-detail-main">
          {/* Description */}
          <section className="sinv-section">
            <h3 className="sinv-section-title">Description</h3>
            {editingContent ? (
              <>
                <textarea
                  className="sinv-textarea"
                  rows={7}
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  aria-label="Description"
                />
                <div className="sinv-inline-actions">
                  <button className="sinv-btn sinv-btn--ghost" onClick={() => setEditingContent(false)}>Annuler</button>
                  <button
                    className="sinv-btn sinv-btn--primary"
                    disabled={!editForm.titre.trim()}
                    onClick={async () => { if (await patch({ titre: editForm.titre, description: editForm.description })) setEditingContent(false); }}
                  >Enregistrer</button>
                </div>
              </>
            ) : demande.description ? (
              <p className="sinv-description">{demande.description}</p>
            ) : (
              <p className="sinv-muted">Pas de description.</p>
            )}
          </section>

          {(isSuperAdmin || documents.length > 0) && (
            <section className="sinv-section">
              <DocumentSection documents={documents} setDocuments={setDocuments} parentId={id} docsApi={supportInnovationDocsApi} canEdit={isSuperAdmin} />
            </section>
          )}

          {/* Échanges */}
          <section className="sinv-section">
            <h3 className="sinv-section-title">Échanges</h3>
            {commentaires.length === 0 && (
              <p className="sinv-muted">
                {isSuperAdmin ? 'Aucun échange pour le moment.' : 'L’équipe vous répondra ici si elle a besoin d’une précision.'}
              </p>
            )}
            <div className="sinv-thread">
              {commentaires.map(c => {
                const mine = c.user_id === user?.id;
                const team = c.role === 'super-admin';
                const canEditComment = isSuperAdmin || mine;
                return (
                  <div key={c.id} className={`sinv-bubble${mine ? ' is-mine' : ''}${team && !mine ? ' is-team' : ''}`}>
                    <div className="sinv-bubble-head">
                      <strong>{mine ? 'Vous' : `${c.prenom || ''} ${c.nom || ''}`.trim()}{team && !mine ? ' · Équipe A97' : ''}</strong>
                      <span>{fmtDateTime(c.created_at)}</span>
                    </div>
                    {editingCommentId === c.id ? (
                      <>
                        <textarea className="sinv-textarea" rows={3} value={editCommentText} onChange={e => setEditCommentText(e.target.value)} aria-label="Modifier le message" />
                        <div className="sinv-inline-actions">
                          <button className="sinv-btn sinv-btn--ghost sinv-btn--sm" onClick={() => setEditingCommentId(null)}>Annuler</button>
                          <button className="sinv-btn sinv-btn--primary sinv-btn--sm" onClick={() => saveComment(c.id)} disabled={!editCommentText.trim()}>Enregistrer</button>
                        </div>
                      </>
                    ) : (
                      <div className="sinv-bubble-body">
                        {c.commentaire}
                        {canEditComment && (
                          <button className="sinv-bubble-edit" title="Modifier le message" aria-label="Modifier le message"
                            onClick={() => { setEditingCommentId(c.id); setEditCommentText(c.commentaire); }}>
                            {Icon.edit(13)}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>
            <div className="sinv-composer">
              <textarea
                rows={2}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onPaste={onCommentPaste}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendComment(); }}
                placeholder={isSuperAdmin && !isMine ? 'Répondre au demandeur…' : 'Ajouter une précision… (Ctrl+V pour coller une capture)'}
                aria-label="Votre message"
              />
              <button className="sinv-btn sinv-btn--primary" onClick={sendComment} disabled={posting || !newComment.trim()}>
                {Icon.send(16)} {posting ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
            {pasteNote && <p className="sinv-help" role="status">{pasteNote}</p>}
          </section>
        </div>

        {isSuperAdmin && (
          <AdminPanel
            demande={demande}
            editMode={editMode}
            commitRefs={commitRefs}
            onPatch={patch}
            onEditContent={() => { setEditForm({ titre: demande.titre, description: demande.description || '' }); setEditingContent(true); }}
            onPromptGenerated={(prompt) => setDemande(prev => ({ ...prev, prompt_genere: prompt }))}
            onReload={load}
            onChanged={onChanged}
            onOpenTicket={onOpenTicket}
            onCreateSubticket={onCreateSubticket}
            onDelete={remove}
          />
        )}
      </div>
    </article>
  );
}

// ─── Panneau de pilotage (super-admin) ───────────────────────────────────────
function AdminPanel({ demande, editMode, commitRefs, onPatch, onEditContent, onPromptGenerated, onReload, onChanged, onOpenTicket, onCreateSubticket, onDelete }) {
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [attachQuery, setAttachQuery] = useState('');
  const [allDemandes, setAllDemandes] = useState([]);

  const children = demande.children || [];
  const childrenDone = children.filter(c => ['valide', 'resolu'].includes(c.statut)).length;

  const generate = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      const r = await supportInnovationApi.generatePrompt(demande.id, demande.page ? pageLabel(demande.page) : '');
      onPromptGenerated(r.data.prompt);
    } catch (err) {
      setGenError(err.response?.data?.error || 'Génération indisponible.');
    } finally {
      setGenLoading(false);
    }
  };

  const copyPrompt = async () => {
    await copyToClipboard(demande.prompt_genere || '');
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 1600);
  };

  const toggleAttach = async () => {
    if (!showAttach) {
      try {
        const r = await supportInnovationApi.getAll({});
        setAllDemandes(r.data.demandes || []);
      } catch { /* liste indisponible */ }
    }
    setShowAttach(s => !s);
    setAttachQuery('');
  };

  const attach = async (childId) => {
    try {
      await supportInnovationApi.setParent(childId, demande.id);
      setShowAttach(false);
      await onReload();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const detach = async (childId) => {
    if (!window.confirm('Retirer ce sous-ticket ? Il redeviendra une demande autonome.')) return;
    try {
      await supportInnovationApi.setParent(childId, null);
      await onReload();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const q = attachQuery.toLowerCase().replace('#', '').trim();
  const attachable = allDemandes.filter(d =>
    d.id !== demande.id && !d.parent_ticket_id && !children.some(c => c.id === d.id) &&
    (!q || (d.titre || '').toLowerCase().includes(q) || String(d.ticket_number || '').padStart(5, '0').includes(q))
  ).slice(0, 30);

  return (
    <aside className="sinv-admin" aria-label="Pilotage">
      <div className="sinv-admin-block">
        <h3 className="sinv-section-title">Statut</h3>
        <div className="sinv-statut-grid">
          {STATUTS.map(s => (
            <button
              key={s.value}
              className={`sinv-statut-btn${demande.statut === s.value ? ' is-active' : ''}`}
              style={{ '--statut-color': s.color }}
              aria-pressed={demande.statut === s.value}
              onClick={() => demande.statut !== s.value && onPatch({ statut: s.value })}
            >
              <span className="sinv-dot" />{s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="sinv-admin-block">
        <h3 className="sinv-section-title">Classement</h3>
        <div className="sinv-segmented" role="group" aria-label="Type">
          {Object.values(TYPES).map(tp => (
            <button key={tp.key} className={demande.type === tp.key ? 'is-active' : ''} aria-pressed={demande.type === tp.key}
              onClick={() => demande.type !== tp.key && onPatch({ type: tp.key })}>
              {tp.key === 'support' ? Icon.bug(15) : Icon.idea(15)} {tp.label}
            </button>
          ))}
        </div>
        <label className="sinv-field-label" htmlFor="sinv-admin-page">Page concernée</label>
        <select id="sinv-admin-page" className="sinv-select" value={demande.page || ''} onChange={e => onPatch({ page: e.target.value || null })}>
          <option value="">Non précisée</option>
          {PAGES.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        <label className="sinv-field-label" htmlFor="sinv-admin-date">Livraison prévue (visible par le demandeur)</label>
        <input
          id="sinv-admin-date"
          type="date"
          className="sinv-select"
          value={demande.date_livraison_prevue ? String(demande.date_livraison_prevue).slice(0, 10) : ''}
          onChange={e => onPatch({ date_livraison_prevue: e.target.value || null })}
        />
        <button className="sinv-btn sinv-btn--ghost sinv-btn--block" onClick={onEditContent}>{Icon.edit(15)} Modifier le titre et la description</button>
        {demande.scope_override && (
          <p className="sinv-callout sinv-callout--warning">
            Le contrôle avait signalé une demande « matériel / hors CRM » ; l’auteur a confirmé qu’elle concerne A97.
          </p>
        )}
        {demande.modifier_prenom && (
          <p className="sinv-help">Modifiée par {demande.modifier_prenom} {demande.modifier_nom} le {formatDate(demande.last_modified_at)}</p>
        )}
      </div>

      <div className="sinv-admin-block sinv-prompt">
        <div className="sinv-prompt-head">
          <h3 className="sinv-section-title">{Icon.wand(15)} Prompt Claude Code</h3>
          {demande.prompt_genere && (
            <button className="sinv-link sinv-link--small" onClick={copyPrompt}>{Icon.copy(13)} {promptCopied ? 'Copié ✓' : 'Copier'}</button>
          )}
        </div>
        {demande.prompt_genere ? (
          <pre className="sinv-prompt-text">{demande.prompt_genere}</pre>
        ) : (
          <p className="sinv-help">
            {demande.type === 'support'
              ? 'Claude Opus 5.5 rédige un prompt de diagnostic et de correction, prêt à coller dans Claude Code.'
              : 'Claude Opus 5.5 rédige un prompt d’implémentation, prêt à coller dans Claude Code.'}
          </p>
        )}
        {genError && <p className="sinv-callout sinv-callout--error" role="alert">{genError}</p>}
        <button className="sinv-btn sinv-btn--primary sinv-btn--block" onClick={generate} disabled={genLoading}>
          {genLoading ? <><span className="sinv-spinner" aria-hidden="true" /> Rédaction en cours…</> : (demande.prompt_genere ? 'Régénérer le prompt' : 'Générer le prompt')}
        </button>
      </div>

      <div className="sinv-admin-block">
        <div className="sinv-prompt-head">
          <h3 className="sinv-section-title">Sous-tickets {children.length > 0 && <span className="sinv-count">{childrenDone}/{children.length}</span>}</h3>
        </div>
        {children.length > 0 && (
          <>
            <div className="sinv-progress"><span style={{ width: `${Math.round((childrenDone / children.length) * 100)}%` }} /></div>
            <ul className="sinv-mini-list">
              {children.map(c => (
                <li key={c.id}>
                  <button className="sinv-mini-item" onClick={() => onOpenTicket(c.id)}>
                    <span className="sinv-dot" style={{ background: statutInfo(c.statut).color }} title={statutInfo(c.statut).label} />
                    <span className="sinv-num">{ticketNum(c.ticket_number)}</span>
                    <span className="sinv-mini-title">{c.titre}</span>
                  </button>
                  <button className="btn-icon" title="Retirer ce sous-ticket" aria-label="Retirer ce sous-ticket" onClick={() => detach(c.id)}>✕</button>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="sinv-inline-actions">
          <button className="sinv-btn sinv-btn--ghost sinv-btn--sm" onClick={toggleAttach}>{showAttach ? 'Fermer' : '+ Rattacher'}</button>
          <button className="sinv-btn sinv-btn--ghost sinv-btn--sm" onClick={() => onCreateSubticket(demande.id)}>+ Créer</button>
        </div>
        {showAttach && (
          <div className="sinv-attach-search">
            <input className="sinv-select" autoFocus placeholder="Titre ou n°…" value={attachQuery} onChange={e => setAttachQuery(e.target.value)} aria-label="Rechercher une demande à rattacher" />
            <ul className="sinv-mini-list">
              {attachable.map(d => (
                <li key={d.id}>
                  <button className="sinv-mini-item" onClick={() => attach(d.id)}>
                    <span className="sinv-dot" style={{ background: statutInfo(d.statut).color }} />
                    <span className="sinv-num">{ticketNum(d.ticket_number)}</span>
                    <span className="sinv-mini-title">{d.titre}</span>
                  </button>
                </li>
              ))}
              {attachable.length === 0 && <li className="sinv-help">Aucune demande autonome trouvée.</li>}
            </ul>
          </div>
        )}
      </div>

      {commitRefs.length > 0 && (
        <div className="sinv-admin-block">
          <h3 className="sinv-section-title">Déploiements ({commitRefs.length})</h3>
          <ul className="sinv-deploys">
            {commitRefs.map(r => (
              <li key={r.commit_hash}>
                <code>{r.commit_hash.slice(0, 7)}</code>
                <span className="sinv-help">{formatDate(r.deployed_at)}{r.branch ? ` · ${r.branch}` : ''}</span>
                <span className="sinv-deploy-msg">{(r.commit_message || '').split('\n')[0]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editMode && (
        <div className="sinv-admin-block">
          <button className="sinv-btn sinv-btn--danger sinv-btn--block" onClick={onDelete}>🗑️ Supprimer la demande</button>
        </div>
      )}
    </aside>
  );
}
